import { z } from "zod";
import { IdSchema, PrincipalSchema, TimestampSchema } from "./primitives.ts";
import { ProposalSchema } from "./resolution.ts";

/** Address for broadcasting a message to every live session. */
export const BROADCAST = "broadcast";

/**
 * What a message does in the negotiation protocol.
 * - `propose_split` / `propose_sequence` / `propose_handoff`: offer a settlement. Needs `overlapId` and a `proposal` with the matching outcome.
 * - `counter`: reply to a proposal with a different one (any outcome, including `shared`). Needs `inReplyTo` and `proposal`.
 * - `accept`: accept the proposal or counter named by `inReplyTo`. The coordinator records the resolution.
 * - `reject`: decline the proposal or counter named by `inReplyTo`. The overlap stays in negotiation.
 * - `yield`: concede the overlap: the other side goes first. The coordinator records a `sequence` resolution.
 * - `info`: anything else (status, heads-up, coordinator notices). No protocol effect.
 */
export const MessageTypeSchema = z.enum([
  "propose_split",
  "propose_sequence",
  "propose_handoff",
  "accept",
  "counter",
  "reject",
  "yield",
  "info",
]);
/** See {@link MessageTypeSchema}. */
export type MessageType = z.infer<typeof MessageTypeSchema>;

const PROPOSAL_OUTCOME: Partial<Record<MessageType, string>> = {
  propose_split: "split",
  propose_sequence: "sequence",
  propose_handoff: "handoff",
};

/**
 * Checks the per-type rules shared by {@link MessageSchema} and the postMessage input.
 * Exported so transports can reuse it.
 */
export function messageShapeIssues(m: {
  type: MessageType;
  overlapId?: string | undefined;
  inReplyTo?: string | undefined;
  proposal?: { outcome: string } | undefined;
}): string[] {
  const issues: string[] = [];
  const expected = PROPOSAL_OUTCOME[m.type];
  if (expected) {
    if (!m.overlapId) issues.push(`${m.type} needs overlapId`);
    if (m.proposal?.outcome !== expected) issues.push(`${m.type} needs a proposal with outcome '${expected}'`);
  }
  if (m.type === "counter" && !m.proposal) issues.push("counter needs a proposal");
  if (["accept", "reject", "counter"].includes(m.type) && !m.inReplyTo) issues.push(`${m.type} needs inReplyTo`);
  if (["accept", "reject", "counter", "yield"].includes(m.type) && !m.overlapId) issues.push(`${m.type} needs overlapId`);
  if (!expected && m.type !== "counter" && m.proposal) issues.push(`${m.type} must not carry a proposal`);
  return issues;
}

/**
 * One message on the negotiation channel. Direct (to a session) or broadcast.
 * Messages about an overlap carry its `overlapId`, which threads them on the dashboard.
 */
export const MessageSchema = z
  .object({
    /** Coordinator-assigned id. Use it as `inReplyTo` and as the `after` cursor when reading. */
    id: IdSchema,
    /** Sender: a session id, `human:<handle>` or `system:<name>`. */
    from: PrincipalSchema,
    /** Recipient: a session id, or `broadcast` for every live session. */
    to: z.union([z.literal(BROADCAST), IdSchema]),
    /** The overlap this message is about, if any. */
    overlapId: IdSchema.optional(),
    /** What the message does, see {@link MessageTypeSchema}. */
    type: MessageTypeSchema,
    /** Human-readable text. Explain your reasoning; the other agent reads this. */
    body: z.string().min(1).max(8000),
    /** The message being answered (required for accept, reject and counter). */
    inReplyTo: IdSchema.optional(),
    /** The settlement on offer (required for proposals and counters). */
    proposal: ProposalSchema.optional(),
    /** When the coordinator accepted the message. */
    ts: TimestampSchema,
  })
  .superRefine((m, ctx) => {
    for (const message of messageShapeIssues(m)) ctx.addIssue({ code: "custom", message });
  });
/** See {@link MessageSchema}. */
export type Message = z.infer<typeof MessageSchema>;
